import React, { PropsWithChildren } from 'react'
import { TrainingEvent, TrainingEventUser } from '@alucio/aws-beacon-amplify/src/models'
import { AppDispatch } from 'src/state/redux'

import {
  SubmitHandler,
  useForm,
  UseFormReturn,
} from 'react-hook-form'
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux'

import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import ActiveUser from 'src/state/global/ActiveUser'
import { trainingEventActions } from 'src/state/redux/slice/trainingEvent';
import { GenericToast, ToastOrientations, useToast } from '@alucio/lux-ui';

import * as logger from 'src/utils/logger';

export interface TrainingEventForm {
  title: string,
  startDateTime: Date,
  endDateTime: Date,
  selectedUsers: TrainingEventUser[]
}

interface TrainingEventFormContext {
  rhForm: UseFormReturn<TrainingEventForm>,
  submitForm: SubmitHandler<TrainingEventForm>,
}

interface TrainingEventFormProviderProps {
  tenantId: string
  toggleModal: () => void
  trainingEvent?: TrainingEvent
}

function roundToNearestHour(date: Date): Date {
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1); // we are rounding to the next hour
  return date;
}

const getDefaultValues = (trainingEvent?: TrainingEvent): TrainingEventForm => {
  if (trainingEvent) {
    return {
      title: trainingEvent.title,
      startDateTime: new Date(trainingEvent.startDateTime),
      endDateTime: new Date(trainingEvent.endDateTime),
      selectedUsers: trainingEvent.trainingEventUsers as TrainingEventUser[],
    }
  } else {
    return {
      title: '',
      startDateTime: roundToNearestHour(new Date()),
      endDateTime: roundToNearestHour(new Date(new Date().getTime() + 60 * 60 * 1000)), // add one hour the end time
      selectedUsers: [] as TrainingEventUser[],
    }
  }
}

const TrainingEventFormContext = React.createContext<TrainingEventFormContext>(undefined!)

export const TrainingEventFormProvider: React.FC<PropsWithChildren<TrainingEventFormProviderProps>> = ({
  children,
  trainingEvent,
  tenantId,
  toggleModal,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast()

  const formSchema = z.object({
    title: z.string().min(1, { message: 'This field is required' }),
    startDateTime: z.date(),
    endDateTime: z.date(),
  }).refine(data => data.endDateTime > data.startDateTime, {
    message: 'End date time must be after start date',
    path: ['endDateTime'],
  })

  const submitForm: SubmitHandler<TrainingEventForm> = () => {
    try {
      const values = rhForm.getValues()
      const now = new Date().toISOString()
      const user = ActiveUser.user
      const payload = {
        title: values.title,
        startDateTime: values.startDateTime.toISOString(),
        endDateTime: values.endDateTime.toISOString(),
        trainingEventUsers: values.selectedUsers,
        tenantId,
        createdAt: now,
        updatedAt: now,
        createdBy: user?.email || 'Alucio Admin',
        updatedBy: user?.email || 'Alucio Admin',
        id: trainingEvent?.id || uuid(),
      }

      if (trainingEvent) {
        const updatedPayload = {
          ...trainingEvent,
          ...payload,
        }
        dispatch(trainingEventActions.editTrainingEvent(updatedPayload))
      } else {
        dispatch(trainingEventActions.saveTrainingEvent(payload as TrainingEvent))
        dispatch(trainingEventActions.add(payload))
      }
      toast.add(
        <GenericToast
          title={`Training event ${trainingEvent ? 'updated' : 'created'} successfully`}
          status="success"
        />,
        ToastOrientations.TOP_RIGHT,
      )
      toggleModal()
    } catch (err) {
      logger.trainingEvent.error(`error ${trainingEvent ? 'updated' : 'created'} training event`, err)
      toast.add(
        <GenericToast
          title={`Unable to ${trainingEvent ? 'updated' : 'created'} training event`}
          status="error"
        />,
        ToastOrientations.TOP_RIGHT,
      )
    }
  }

  const defaultValues = getDefaultValues(trainingEvent)
  const rhForm = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues,
  })
  const value: TrainingEventFormContext = {
    rhForm,
    submitForm,
  }
  return (
    <TrainingEventFormContext.Provider value={value}>
      {children}
    </TrainingEventFormContext.Provider>
  )
}

export const useTrainingEventForm = () => {
  const context = React.useContext(TrainingEventFormContext)
  if (!context) {
    throw new Error('useTrainingEventForm must be used within the TrainigEventFormProvider')
  }
  return context
}
